import { get, post, put, del, patch, postExport, getExport } from "helpers/api_helper"
import { cachedGet, invalidateCacheTags } from "helpers/requestCache"
import {
  eventCacheTags,
  invalidateEventCache,
  invalidateEventCacheAfterSuccess,
  normalizeEventCacheId,
  normalizeObjectId,
} from "./eventCachePolicy"
import {
  createEventsListUrl,
  createEventsLocationsUrl,
} from "./eventListQueryPolicy"

const EVENT_LIST_CACHE_TTL_MS = 60 * 1000
const EVENT_LOCATION_CACHE_TTL_MS = 10 * 60 * 1000
const EVENT_DETAIL_CACHE_TTL_MS = 30 * 1000
const EVENT_SHELL_CACHE_TTL_MS = 30 * 1000
const EVENT_ANNOUNCEMENTS_CACHE_TTL_MS = 30 * 1000
const EVENT_COMPETITION_CACHE_TTL_MS = 30 * 1000
const ASYNC_STATUS_CACHE_INVALIDATION_DELAY_MS = 5000

const isAsyncRequestAccepted = response =>
  response?.message === "REQUEST_SENT" ||
  response?.data?.message === "REQUEST_SENT"

const invalidateEventCacheAfterAsyncStatusUpdate = (response, identity) => {
  if (!isAsyncRequestAccepted(response)) return

  setTimeout(() => {
    invalidateEventCache(identity)
  }, ASYNC_STATUS_CACHE_INVALIDATION_DELAY_MS)
}

export const getPenalties = async payload => {
  const [pageOptions, filterOptions] = payload?.payload
  const filters = new URLSearchParams(filterOptions).toString()
  const response = await get(`/admin/penalties?${filters}&limit=${pageOptions?.limit}&page=${pageOptions?.page}`)
  if (response?.success === false || !Array.isArray(response?.data?.penalties)
    || !Number.isSafeInteger(response?.data?.length) || response.data.length < 0) {
    throw new Error("Invalid penalties response")
  }
  return response
}

export const getEventsLocationsApi = async (filters, time) => {
  let response
  try {
    response = await cachedGet(createEventsLocationsUrl({
      filters,
      timeframe: time,
    }), {
      ttlMs: EVENT_LOCATION_CACHE_TTL_MS,
      tags: [eventCacheTags.all, eventCacheTags.locations],
      fetcher: get,
    })
  } catch (error) {
    console.log(error)
  }
  // The facets feed wraps the list as { data: { locations } }; callers expect the list in data.
  return response ? { ...response, data: response.data?.locations ?? [] } : response
}

export const getEvents = async (filters, options, time) => {
    const obj1 = typeof filters === "string" ? JSON.parse(filters) : filters
    const obj2 = typeof options === "string" ? JSON.parse(options) : options
    const queryKey = createEventsListUrl({ filters: obj1, options: obj2, timeframe: time })
    const response = await cachedGet(createEventsListUrl({
      filters: obj1,
      options: obj2,
      timeframe: time,
    }), {
      ttlMs: EVENT_LIST_CACHE_TTL_MS,
      tags: [eventCacheTags.all, eventCacheTags.lists],
      fetcher: get,
    })
    if (!Array.isArray(response?.data?.results) || typeof response.data.hasNextPage !== "boolean") {
      throw new Error("Invalid events response")
    }
    return { ...response, data: { ...response.data, queryKey } }
}

// The list read again past its cache, which keeps the fresh read: a list follows its texts while they translate (ADR 0013).
export const refreshEvents = (filters, options, time) => {
  invalidateCacheTags([eventCacheTags.lists])
  return getEvents(filters, options, time)
}

export const getEventDetails = async id => {
  let response
  try {
    const eventId = normalizeEventCacheId(id)

    if (!eventId) {
      response = await get(`/events/${id}`)
    } else {
      response = await cachedGet(`/events/${eventId}`, {
        ttlMs: EVENT_DETAIL_CACHE_TTL_MS,
        key: `event-detail:${eventId}`,
        tags: [
          eventCacheTags.all,
          eventCacheTags.event(eventId),
          eventCacheTags.detail(eventId),
          eventCacheTags.shell(eventId),
          eventCacheTags.competitions(eventId),
          eventCacheTags.entryCounts(eventId),
        ],
        fetcher: get,
      })
    }
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getEventShell = async id => {
  let response
  try {
    const eventId = normalizeEventCacheId(id)

    if (!eventId) return response

    response = await cachedGet(`/admin/events/${eventId}/shell`, {
      ttlMs: EVENT_SHELL_CACHE_TTL_MS,
      key: `event-shell:${eventId}`,
      tags: [
        eventCacheTags.all,
        eventCacheTags.event(eventId),
        eventCacheTags.shell(eventId),
        eventCacheTags.competitions(eventId),
        eventCacheTags.entryCounts(eventId),
      ],
      fetcher: get,
    })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getEventAnnouncements = async id => {
  let response
  try {
    const eventId = normalizeEventCacheId(id)

    if (!eventId) return response

    response = await cachedGet(`/admin/events/${eventId}/announcements`, {
      ttlMs: EVENT_ANNOUNCEMENTS_CACHE_TTL_MS,
      key: `event-announcements:${eventId}`,
      tags: [eventCacheTags.all, eventCacheTags.event(eventId)],
      fetcher: get,
    })
  } catch (error) {
    console.log(error)
  }
  return response
}

// The event's announcements as the API holds them now, past the cache: a translation that runs, or a command's answer that was lost.
export const readEventAnnouncements = id => get(`/admin/events/${normalizeEventCacheId(id)}/announcements`)

export const getEventCompetition = async (id, competitionId) => {
  let response
  try {
    const eventId = normalizeEventCacheId(id)
    const normalizedCompetitionId = normalizeObjectId(competitionId)

    if (!eventId || !normalizedCompetitionId) return response

    response = await cachedGet(
      `/admin/events/${eventId}/competitions/${normalizedCompetitionId}`,
      {
        ttlMs: EVENT_COMPETITION_CACHE_TTL_MS,
        key: `event-competition:${eventId}:${normalizedCompetitionId}`,
        tags: [
          eventCacheTags.all,
          eventCacheTags.event(eventId),
          eventCacheTags.competitions(eventId),
          eventCacheTags.competition(normalizedCompetitionId),
          eventCacheTags.competitionDetail(normalizedCompetitionId),
          eventCacheTags.competitionEntries(normalizedCompetitionId),
          eventCacheTags.competitionLists(normalizedCompetitionId),
          eventCacheTags.competitionDraws(normalizedCompetitionId),
          eventCacheTags.competitionMatches(normalizedCompetitionId),
          eventCacheTags.competitionLogs(normalizedCompetitionId),
          eventCacheTags.competitionPoints(normalizedCompetitionId),
          eventCacheTags.competitionPenalties(normalizedCompetitionId),
        ],
        fetcher: get,
      }
    )
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getRegistrationQuote = ({
  eventId,
  competitionId,
  payerUserId,
  partnerUserId,
  slotType,
}) => {
  const query = new URLSearchParams({
    payerUserId: String(payerUserId),
  })

  if (partnerUserId !== undefined && partnerUserId !== null) {
    query.set("partnerUserId", String(partnerUserId))
  }

  if (slotType) query.set("slotType", slotType)

  return get(
    `/admin/events/${eventId}/competitions/${competitionId}/registration-quote?${query.toString()}`, { timeout: 15000 }
  )
}

export const updateEventStatus = async (id, type, socketClientId) => {
  let response
  try {
    response = await put(`/admin/events/${id}/status/${type}`, {
      socketClientId,
    })
    invalidateEventCacheAfterSuccess(response, { eventId: id })
    invalidateEventCacheAfterAsyncStatusUpdate(response, { eventId: id })
  } catch (error) {
    console.log(error)
  }
  return response
}

/**
 * Replaces the sponsors of one event with the list given — the WHOLE list, every save (an empty
 * list removes them all). The gateway answers 202 and the stored list, ids minted, comes back on
 * the socket as `events-response` / SUCCESSFULLY_UPDATED_EVENT_SPONSORS. The save carries
 * `correlationId` as X-Correlation-Id, and Events names the event and that correlation in every
 * reply (success or refusal), so the tab acts only on the answer to its own save. Unlike the rest
 * of this file it lets a failure through: the tab tells the admin why in words rather than a
 * silent no-op. Invalidated twice, as a status change is: now, and again once the broker has had
 * time to apply the write, so a read that raced it does not pin the old list in the cache for its TTL.
 */
export const updateEventSponsors = async ({ eventId, sponsors, socketClientId, correlationId }) => {
  const response = await put(`/admin/events/${eventId}/sponsors`, {
    socketClientId,
    sponsors,
  }, ...(correlationId ? [{ headers: { "X-Correlation-Id": correlationId } }] : []))
  invalidateEventCacheAfterSuccess(response, { eventId })
  invalidateEventCacheAfterAsyncStatusUpdate(response, { eventId })
  return response
}

export const deleteEvent = async id => {
  let response
  try {
    response = await del(`/admin/events/${id}`)
    invalidateEventCacheAfterSuccess(response, { eventId: id })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const createCompetition = async ({ id, ...data }) => {
  let response
  try {
    response = await post(`/admin/events/${id}/competitions`, data)
    invalidateEventCacheAfterSuccess(response, { eventId: id })
  } catch (error) {
    console.log(error)
  }

  return response
}

export const deleteCompetition = async (id, competitionId, socketClientId) => {
  let response

  try {
    response = await del(
      `/admin/events/${id}/competitions/${competitionId}/status`,
      { socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId: id, competitionId })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const enableCompetition = async (
  id,
  competitionId,
  type,
  socketClientId
) => {
  let response

  try {
    response = await patch(
      `/admin/events/${id}/competitions/${competitionId}/status/${type}`,
      { socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId: id, competitionId })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const increaseWildeCards = async ({
  id,
  socketClientId,
  competitionId,
}) => {
  let response

  try {
    response = await patch(
      `/admin/events/${id}/competitions/${competitionId}/wildCards/plus`,
      { socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId: id, competitionId })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const decreaseWildeCards = async ({
  id,
  socketClientId,
  competitionId,
}) => {
  let response

  try {
    response = await patch(
      `/admin/events/${id}/competitions/${competitionId}/wildCards/minus`,
      { socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId: id, competitionId })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const editCompetition = async ({ id, competitionId, ...data }) => {
  let response

  try {
    response = await patch(
      `/admin/events/${id}/competitions/${competitionId}`,
      data
    )
    invalidateEventCacheAfterSuccess(response, { eventId: id, competitionId })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const addEntry = async ({
  preRegistrationList,
  competitionId,
  id,
  socketClientId,
  wild,
  player: { data: player } = {},
  partner: { data: partner } = {},
}) => {
  let response

  let slotType = wild ? "WILD_CARD" : "STANDARD"

  try {
    response = await post(
      `/admin/events/${id}/competitions/${competitionId}/${
        preRegistrationList ? "preEntries" : `entries/${slotType}`
      }`,
      { player, partner, socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId: id, competitionId })
  } catch (error) {
    console.log(error)
  }
  return response
}

export const deleteEntry = async ({
  competitionId,
  eventId,
  socketClientId,
  entryId,
}) => {
  let response
  try {
    response = await del(
      `/admin/events/${eventId}/competitions/${competitionId}/entries/${entryId}`,
      { socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId, competitionId })
  } catch (error) {
    console.log(error)
  }

  return response
}

export const moveEntry = async ({
  competitionId,
  eventId,
  entryId,
  list,
  socketClientId,
}) => {
  let response

  try {
    response = await put(
      `/admin/events/${eventId}/competitions/${competitionId}/entries/${entryId}/${list}`,
      { socketClientId }
    )
    invalidateEventCacheAfterSuccess(response, { eventId, competitionId })
  } catch (error) {
    console.log(error)
  }

  return response
}

export const setLuckyDraw = async ({
  competitionId,
  eventId,
  socketClientId,
  operationId,
  correlationId,
  phaseId,
  confirmResultsLoss,
}) => {
  let response

  try {
    response = await post(
      `/admin/events/${eventId}/competitions/${competitionId}/draw${
        phaseId ? `/${phaseId}` : ""
      }`,
      { socketClientId, ...(operationId ? { operationId } : {}), ...(confirmResultsLoss ? { confirmResultsLoss: true } : {}) },
      ...(correlationId ? [{ headers: { "X-Correlation-Id": correlationId } }] : [])
    )
    invalidateEventCacheAfterSuccess(response, { eventId, competitionId })
  } catch (error) {
    if (operationId) throw error
    console.log(error)
  }

  return response
}

export const setMainDraw = async ({
  competitionId,
  eventId,
  socketClientId,
  operationId,
  correlationId,
  phaseId,
  confirmResultsLoss,
}) => {
  let response

  try {
    response = await post(
      `/admin/events/${eventId}/competitions/${competitionId}/mainDraw${
        phaseId ? `/${phaseId}` : ""
      }`,
      { socketClientId, ...(operationId ? { operationId } : {}), ...(confirmResultsLoss ? { confirmResultsLoss: true } : {}) },
      ...(correlationId ? [{ headers: { "X-Correlation-Id": correlationId } }] : [])
    )
    invalidateEventCacheAfterSuccess(response, { eventId, competitionId })
  } catch (error) {
    if (operationId) throw error
    console.log(error)
  }

  return response
}

export const getMainDrawXls = async ({
  competitionId,
  eventId,
  addToast,
  setGeneratingXls,
}) => {
  setGeneratingXls(true);

  let response;

  try {
    response = await getExport(
      `/exports/events/${eventId}/competitions/${competitionId}/main-draw`,
      { responseType: "blob" }
    )

    // Extract filename
    let filename = 'main-draw.xlsx';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.includes('filename=')) {
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      filename = filenameMatch ? filenameMatch[1] : filename;
    }

    // Use response.data as Blob
    const blob = response.data;

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    addToast("Download started...", {
      appearance: "success",
      autoDismiss: true,
    });
  } catch (error) {
    addToast("The Excel file could not be downloaded. Please try again.", {
      appearance: "error",
      autoDismiss: true,
    })
  } finally {
    setGeneratingXls(false)
  }
}

export const updateLuckyDraw = async ({
  competitionId,
  eventId,
  socketClientId,
  operationId,
  correlationId,
  ...details
}) => {
  let response

  try {
    response = await patch(
      `/admin/events/${eventId}/competitions/${competitionId}/draw`,
      { socketClientId, ...details, ...(operationId ? { operationId } : {}) },
      ...(correlationId ? [{ headers: { "X-Correlation-Id": correlationId } }] : [])
    )
    invalidateEventCacheAfterSuccess(response, { eventId, competitionId })
  } catch (error) {
    if (operationId) throw error
    console.log(error)
  }

  return response
}


export const generateGroupPhaseMatches = async ({
  competitionId,
  eventId,
  socketClientId,
  phaseInfo,
  setGeneratingPhaseMatches,
  resetPhase = false,
}) => {
  let response

  try {
    response = await post(
      `/admin/events/${eventId}/competitions/${competitionId}/phase-matches`,
      {
        socketClientId,
        phaseInfo,
        resetPhase,
      }
    )
    invalidateEventCacheAfterSuccess(response, { eventId, competitionId })
  } catch (error) {
    setGeneratingPhaseMatches(false);
    console.log(error)
  }


  return response
}
